import { usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode, podcastAuditToolNode, faqPageNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { PodcastAuditForm } from "@/components/marketing/PodcastAuditForm"

const DESCRIPTION =
  "Free podcast discoverability audit: paste your RSS feed and get an A to F grade from 13 checks of your feed metadata, your Apple Podcasts listing, and your real Apple keyword rankings. No Spotify guesses."

const WHAT_WE_CHECK = [
  {
    title: "Your feed's metadata",
    line: "Show title, author field, categories, subtitle, episode titles — the signals Apple and Spotify actually read to figure out what your show is about.",
  },
  {
    title: "Your Apple Podcasts listing",
    line: "Whether Apple's index can even find your show for its own exact title — the most basic discoverability failure there is, and one most hosts never check.",
  },
  {
    title: "Where you rank",
    line: "We derive a handful of keywords from your feed and search Apple Podcasts for each one, so you see your actual position — not a guess.",
  },
]

// The failures the audit actually tests for (see PodcastAuditRunner). Each
// maps to a check in the runner, in plain language.
const COMMON_FAILURES = [
  {
    title: "Apple's index can't find your show, or lists it twice",
    line: "The audit searches Apple Podcasts once for your exact show title and looks for your feed in the results. If it isn't there, or more than one listing points at your feed (which splits reviews, ratings and search relevance), the grade is capped at D. When Apple can't see the show, the audit skips the rank checks instead of reporting rankings that would mean nothing.",
  },
  {
    title: "A show title and author field with no topic in them",
    line: "The audit looks for a topic word in your show title that your recent episodes or categories also use, and flags an author field that is empty or is just a person's name. Apple weights the author field second only to the show title.",
  },
  {
    title: "Episode titles with nothing searchable in them",
    line: "It reads your last 20 episode titles and counts how many contain a searchable term. Titles like \"#42 - Jane Smith\" give search nothing to match, and under 30% searchable is a fail.",
  },
  {
    title: "Unused categories, a missing subtitle, no topic in the description",
    line: "Three categories is a pass, two a warning, none or one a fail. It also checks for an iTunes subtitle and for a topic term in the first part of the description.",
  },
  {
    title: "A feed that won't load, or is blocked from Apple",
    line: "If the URL returns an error or a web page instead of a feed, the grade is capped at F. A feed with the Apple Podcasts block flag set to yes is capped even lower.",
  },
]

const HOW_TO_READ = [
  "The result is a score out of 100 turned into a letter: A is 90 and up, B is 80 to 89, C is 70 to 79, D is 55 to 69, and F is below 55.",
  "Each check scores pass, warn or fail. A pass earns the check's full weight, a warn earns half, and a fail earns nothing. The weights add up to 100, so the score reads directly as a percentage.",
  "The heaviest checks are whether the feed loads (20 points), whether the show title carries topic keywords (15), whether Apple lists the show (14), and your keyword rankings (12). Smaller checks such as the author field, episode titles, description, categories, publishing cadence, subtitle and transcripts make up the rest.",
  "Rankings are always shown as a position out of the results Apple returned, such as \"#7 of 97\", never a bare number. A rank in a very small pool is labelled as a small pool, not counted as a win or a loss.",
  "Three caps apply. A feed that won't load is capped at 30, a feed blocked from Apple is capped at 25, and a failed Apple listing check (not found, or duplicate listings) is capped at 55.",
  "Every report ends with what was checked (your RSS feed and Apple Podcasts search in the US) and what wasn't (Spotify, and search volume or keyword difficulty).",
]

const FAQS = [
  {
    q: "Is the podcast audit free?",
    a: "Yes. The audit is free, and there is no paid tier of this tool. You enter your RSS feed URL (or Apple Podcasts link) and your email, and we also email you the results.",
  },
  {
    q: "How long does the audit take?",
    a: "It runs in the background after you submit. The results page updates itself and shows your grade once the audit completes, and the same report is emailed to you. The keyword ranking step has a 75-second time budget, so an audit does not hang. You can run up to three audits per hour.",
  },
  {
    q: "What does the audit check?",
    a: "Thirteen checks across three areas: your RSS feed (whether it loads, show title, author field, subtitle, description, categories, episode titles, publishing cadence, transcripts, block flag and migration flag), your Apple Podcasts listing, and your rank in Apple Podcasts search for keywords derived from your feed. It uses public data only.",
  },
  {
    q: "Why doesn't the audit show my Spotify ranking?",
    a: "Spotify doesn't offer a public search API, so there is no real data to check. Apple Podcasts does, which is why the audit covers Apple search. Everything it reports is real, verifiable data, and every report states that Spotify was not checked.",
  },
  {
    q: "What should I do with my result?",
    a: "Start with anything marked fail, beginning with the heaviest checks: whether the feed loads, the show title, the Apple listing and keyword rankings. Each finding in the report explains what was found in your feed. The metadata fixes (author field, categories, subtitle, description, episode titles) are changes to your feed, which you can make in your podcast host.",
  },
  {
    q: "Do I need to log in or give access to my podcast account?",
    a: "No. There is no account and no login. The audit reads your public RSS feed and searches Apple Podcasts, the same things anyone else can see. You only need your feed URL (or Apple Podcasts link) and an email address for the results.",
  },
]

export default function PodcastAudit() {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")

  return (
    <MarketingLayout>
      {/* Graduated from hidden launch 2026-07-30 — the landing page is public
          and indexable. Result pages (PodcastAuditResult) stay noindex: a
          person's grade is theirs, not a search result. */}
      <SeoHead
        title="Free Podcast Discoverability Audit"
        description={DESCRIPTION}
        jsonLd={[organizationNode(base), podcastAuditToolNode(base), faqPageNode(FAQS)]}
      />

      {/* Hero + form */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Podcast Discoverability</Eyebrow>
        <h1 className="display-1 mt-4">Can people actually find your show?</h1>
        <p className="mt-5 text-lg text-ink-body">
          Most podcasters have never checked whether their feed's metadata
          helps or hurts them — or whether Apple Podcasts search can even
          find their show at all. Paste your feed and get a straight answer.
        </p>

        <div className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
          <h2 className="display-2">Run your free audit</h2>
          <p className="mt-2 text-ink-muted">
            Paste your RSS feed URL (or Apple Podcasts link) and your email —
            results show up right here, and we'll also email them to you.
          </p>
          <div className="mt-6">
            <PodcastAuditForm source="audit_page" />
          </div>
        </div>
      </Section>

      {/* What we check */}
      <Section className="bg-surface" containerClassName="max-w-4xl">
        <Eyebrow>What we check</Eyebrow>
        <h2 className="display-2 mt-4">Feed metadata, plus where you rank on Apple</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {WHAT_WE_CHECK.map((item) => (
            <div key={item.title} className="rounded-xl border border-hairline bg-page p-6">
              <h3>{item.title}</h3>
              <p className="mt-2 text-ink-muted">{item.line}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-ink-muted">
          Being upfront about scope: this audit covers Apple Podcasts search,
          because it's the one major platform with a public search API.
          Spotify doesn't expose one, so we can't check your Spotify ranking
          — anyone who tells you they can is guessing. Everything else here
          (feed metadata, Apple listing status, Apple keyword rank) is real,
          verifiable data, and the audit is genuinely free.
        </p>
      </Section>

      {/* Common discoverability failures — only what the runner really tests */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>Common failures</Eyebrow>
        <h2 className="display-2 mt-4">The discoverability problems the audit looks for</h2>
        <ul className="mt-8 grid gap-5">
          {COMMON_FAILURES.map((item) => (
            <li key={item.title} className="rounded-xl border border-hairline bg-surface p-6">
              <h3>{item.title}</h3>
              <p className="mt-2 text-ink-muted">{item.line}</p>
            </li>
          ))}
        </ul>
      </Section>

      {/* How to read your result */}
      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>Your grade</Eyebrow>
        <h2 className="display-2 mt-4">How to read your result</h2>
        <ul className="mt-8 grid gap-4">
          {HOW_TO_READ.map((line) => (
            <li key={line} className="text-ink-body">
              {line}
            </li>
          ))}
        </ul>
      </Section>

      {/* Worked example intentionally omitted: needs a real, consented client
          audit. Add a "what a result looks like" section here once supplied. */}

      {/* FAQ */}
      <Section className="border-t border-hairline" containerClassName="max-w-4xl">
        <Eyebrow>Questions</Eyebrow>
        <h2 className="display-2 mt-4">About the podcast audit</h2>
        <dl className="mt-8 grid gap-5 md:grid-cols-2">
          {FAQS.map((faq) => (
            <div key={faq.q} className="rounded-xl border border-hairline bg-surface p-6">
              <dt>
                <h3>{faq.q}</h3>
              </dt>
              <dd className="mt-3 text-ink-muted">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>
    </MarketingLayout>
  )
}
